import {
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  Input,
  SimpleGrid,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  useBreakpointValue,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, Link } from "@tanstack/react-router"
import { useEffect, useMemo, useRef, useState } from "react"
import {
  InventoryCountApi,
  type InventoryCountTaskLineDTO,
} from "../../../../../api/inventoryCount"
import type { UserPublic } from "../../../../../client"
import ActionAlertDialog from "../../../../../components/WMS/shared/ActionAlertDialog"
import { WmsPageHeader } from "../../../../../components/WMS/shared/WmsPageHeader"
import {
  WmsPagePanel,
  WmsPageShell,
} from "../../../../../components/WMS/shared/WmsPageShell"
import { WmsTableListCard } from "../../../../../components/WMS/shared/WmsTableListCard"
import { hasPermission } from "../../../../../utils/roles"

export const Route = createFileRoute(
  "/_layout/WMS/inventory-count/tasks/$taskId",
)({
  component: InventoryCountTaskDetailPage,
})

function InventoryCountTaskDetailPage() {
  const { taskId } = Route.useParams()
  const qc = useQueryClient()
  const currentUser = qc.getQueryData<UserPublic>(["currentUser"])
  const [editValues, setEditValues] = useState<Record<string, string>>({})
  const [localFormError, setLocalFormError] = useState("")
  const [recountDialogOpen, setRecountDialogOpen] = useState(false)
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false)
  const [recountReason, setRecountReason] = useState(
    "blokada księgowania korekt",
  )
  const [cancelReason, setCancelReason] = useState(
    "zadanie zablokowane przed księgowaniem",
  )
  const [recountDialogError, setRecountDialogError] = useState("")
  const [cancelDialogError, setCancelDialogError] = useState("")
  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({})
  const recountCancelRef = useRef<HTMLButtonElement>(null)
  const cancelCancelRef = useRef<HTMLButtonElement>(null)
  const recountReasonRef = useRef<HTMLTextAreaElement>(null)
  const cancelReasonRef = useRef<HTMLTextAreaElement>(null)
  const toast = useToast()

  useEffect(() => {
    if (!recountDialogOpen && !cancelDialogOpen) return
    const frame = requestAnimationFrame(() => {
      if (recountDialogOpen) recountReasonRef.current?.focus()
      if (cancelDialogOpen) cancelReasonRef.current?.focus()
    })
    return () => cancelAnimationFrame(frame)
  }, [cancelDialogOpen, recountDialogOpen])

  const cardBg = useColorModeValue("white", "gray.800")
  const cardBorder = useColorModeValue("gray.200", "whiteAlpha.200")
  const theadBg = useColorModeValue("gray.100", "whiteAlpha.100")
  const zebraBg = useColorModeValue("blackAlpha.50", "whiteAlpha.50")
  const mutedText = useColorModeValue("gray.500", "gray.400")
  const stickyBg = useColorModeValue("whiteAlpha.950", "gray.900")
  const stickyBorder = useColorModeValue("gray.200", "whiteAlpha.300")
  const isMobile = useBreakpointValue({ base: true, lg: false }) ?? false

  const detailQuery = useQuery({
    queryKey: ["wms", "inventory-count", "tasks", "detail", taskId],
    retry: false,
    queryFn: () => InventoryCountApi.getTaskById(taskId),
  })

  const countMutation = useMutation({
    mutationFn: async (
      payload: Array<{
        line_id: string
        counted_qty: number
        count_state?: string
      }>,
    ) => {
      if (detailQuery.data?.status === "open") {
        await InventoryCountApi.startTask(taskId)
        const refreshedTask = await InventoryCountApi.getTaskById(taskId)
        payload = remapCountPayload(
          payload,
          detailQuery.data.lines,
          refreshedTask.lines,
        )
      }
      return InventoryCountApi.countTaskLines(taskId, payload)
    },
    onError: (error: unknown) => {
      setLocalFormError(parseApiError(error, "Nie udało się zapisać ilości."))
      void qc.invalidateQueries({
        queryKey: ["wms", "inventory-count", "tasks", "detail", taskId],
      })
    },
    onSuccess: async () => {
      setEditValues({})
      setLocalFormError("")
      await qc.invalidateQueries({
        queryKey: ["wms", "inventory-count", "tasks", "detail", taskId],
      })
      await qc.invalidateQueries({
        queryKey: ["wms", "inventory-count", "tasks"],
      })
    },
  })

  const submitMutation = useMutation({
    mutationFn: () => InventoryCountApi.submitTask(taskId),
    onSuccess: async () => {
      setLocalFormError("")
      await qc.invalidateQueries({
        queryKey: ["wms", "inventory-count", "tasks", "detail", taskId],
      })
      await qc.invalidateQueries({
        queryKey: ["wms", "inventory-count", "tasks"],
      })
    },
  })

  const reviewMutation = useMutation({
    mutationFn: (payload: {
      decision: "approve" | "reject"
      reason?: string
    }) => InventoryCountApi.approveTask(taskId, payload),
    onSuccess: async (_data, variables) => {
      if (variables.decision === "reject") {
        setCancelDialogOpen(false)
        setCancelDialogError("")
        setCancelReason("zadanie zablokowane przed księgowaniem")
      }
      await qc.invalidateQueries({
        queryKey: ["wms", "inventory-count", "tasks", "detail", taskId],
      })
      await qc.invalidateQueries({
        queryKey: ["wms", "inventory-count", "tasks"],
      })
      await qc.invalidateQueries({
        queryKey: ["wms", "inventory-count", "locks"],
      })
    },
    onError: (error: unknown, variables) => {
      const message = parseApiError(error, "Nie udało się zmienić decyzji.")
      if (variables.decision === "reject") {
        setCancelDialogError(message)
      } else {
        setLocalFormError(message)
      }
    },
  })
  const recountMutation = useMutation({
    mutationFn: (reason: string) =>
      InventoryCountApi.recountTask(taskId, { reason }),
    onSuccess: async () => {
      setRecountDialogOpen(false)
      setRecountDialogError("")
      setRecountReason("blokada księgowania korekt")
      setLocalFormError("")
      await qc.invalidateQueries({
        queryKey: ["wms", "inventory-count", "tasks", "detail", taskId],
      })
      await qc.invalidateQueries({
        queryKey: ["wms", "inventory-count", "tasks"],
      })
      await qc.invalidateQueries({
        queryKey: ["wms", "inventory-count", "locks"],
      })
    },
    onError: (error: unknown) => {
      setRecountDialogError(
        parseApiError(error, "Nie udało się utworzyć nowej rundy."),
      )
    },
  })
  const postMutation = useMutation({
    mutationFn: () => InventoryCountApi.postTask(taskId),
    onSuccess: async () => {
      setLocalFormError("")
      toast({
        title: "Inwentaryzacja została zaksięgowana",
        status: "success",
        duration: 3000,
        isClosable: true,
      })
      await qc.invalidateQueries({
        queryKey: ["wms", "inventory-count", "tasks", "detail", taskId],
      })
      await qc.invalidateQueries({
        queryKey: ["wms", "inventory-count", "tasks"],
      })
      await qc.invalidateQueries({
        queryKey: ["wms", "inventory-count", "locks"],
      })
      await qc.invalidateQueries({
        queryKey: ["wms", "inventory-count", "kpi"],
      })
      await qc.invalidateQueries({
        queryKey: ["wms", "inventory-count", "alerts"],
      })
    },
  })

  const detail = detailQuery.data
  const lines = detail?.lines ?? []

  const latestLines = useMemo(() => {
    const grouped = new Map<string, (typeof lines)[0]>()
    for (const line of lines) {
      const key = [
        line.location_code,
        line.product_id,
        line.warehouse_lot_id || "",
        line.lot_no || "",
        line.serial_no || "",
      ].join("|")
      const existing = grouped.get(key)
      if (!existing || line.count_round > existing.count_round) {
        grouped.set(key, line)
      }
    }
    return Array.from(grouped.values())
  }, [lines])

  const sortedLines = useMemo(
    () =>
      [...latestLines].sort((a, b) => {
        const byLocation = compareLocationCodes(
          a.location_code,
          b.location_code,
        )
        if (byLocation !== 0) {
          return byLocation
        }
        const bySku = (a.product_sku || "").localeCompare(
          b.product_sku || "",
          "en",
          {
            sensitivity: "base",
            numeric: true,
          },
        )
        if (bySku !== 0) {
          return bySku
        }
        return a.id.localeCompare(b.id)
      }),
    [latestLines],
  )
  const lineIds = useMemo(
    () => sortedLines.map((line) => line.id),
    [sortedLines],
  )

  const changedLines = useMemo(() => {
    return lineIds
      .filter((lineId) => typeof editValues[lineId] === "string")
      .map((lineId) => {
        const parsed = parseCountedQty(editValues[lineId])
        if (parsed.error || typeof parsed.value !== "number") {
          return null
        }
        return {
          line_id: lineId,
          counted_qty: parsed.value,
          count_state: "counted",
        }
      })
      .filter(
        (
          line,
        ): line is {
          line_id: string
          counted_qty: number
          count_state: string
        } => line !== null,
      )
  }, [editValues, lineIds])

  const canCount =
    (detail?.status === "open" || detail?.status === "in_progress") &&
    hasPermission(currentUser, "wms.inventory_count.task.execute")
  const canReview =
    detail?.status === "pending_review" &&
    hasPermission(currentUser, "wms.inventory_count.task.approve")
  const canRecount =
    (detail?.status === "pending_review" || detail?.status === "approved") &&
    hasPermission(currentUser, "wms.inventory_count.task.recount")
  const canCancel =
    (detail?.status === "pending_review" || detail?.status === "approved") &&
    hasPermission(currentUser, "wms.inventory_count.task.approve")
  const canPost =
    detail?.status === "approved" &&
    hasPermission(currentUser, "wms.inventory_count.task.post")

  const lineErrors = useMemo(() => {
    const errors: Record<string, string> = {}
    if (!canCount) {
      return errors
    }
    sortedLines.forEach((line) => {
      const currentRaw =
        editValues[line.id] ??
        (typeof line.counted_qty === "number" ? String(line.counted_qty) : "")
      const parsed = parseCountedQty(currentRaw)
      if (parsed.error) {
        errors[line.id] = parsed.error
      }
    })
    return errors
  }, [canCount, editValues, sortedLines])

  const hasDirtyInvalidValues = Object.keys(editValues).some((lineId) =>
    Boolean(lineErrors[lineId]),
  )
  const canSave = canCount && changedLines.length > 0 && !hasDirtyInvalidValues
  const hasUnsavedChanges = changedLines.length > 0
  const canSubmit =
    canCount &&
    latestLines.length > 0 &&
    Object.keys(lineErrors).length === 0 &&
    !hasUnsavedChanges

  const focusNext = (lineId: string) => {
    const currentIndex = lineIds.indexOf(lineId)
    if (currentIndex < 0) {
      return
    }
    const nextId = lineIds[currentIndex + 1]
    if (!nextId) {
      return
    }
    inputRefs.current[nextId]?.focus()
    inputRefs.current[nextId]?.select()
  }

  return (
    <WmsPageShell>
      <WmsPageHeader
        title={detail ? `Zadanie ${detail.task_no}` : "Szczegóły zadania"}
        description="Ekran liczenia oraz weryfikacji i zatwierdzania dla konkretnego zadania inwentaryzacji."
        gradient="linear(to-r, cyan.600, blue.700)"
        badge={detail?.status ?? "ładowanie"}
      />

      <WmsPagePanel bg={cardBg} borderColor={cardBorder}>
        <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={3}>
          <StatCard
            label="Status"
            value={detail?.status ?? "—"}
            mutedText={mutedText}
          />
          <StatCard
            label="Zakres"
            value={detail?.scope_type ?? "—"}
            mutedText={mutedText}
          />
          <StatCard
            label="Priorytet"
            value={String(detail?.priority ?? "—")}
            mutedText={mutedText}
          />
          <StatCard
            label="Pozycje"
            value={String(latestLines.length)}
            mutedText={mutedText}
          />
        </SimpleGrid>

        <Flex gap={3} wrap="wrap" mt={1}>
          <Button
            as={Link}
            to="/WMS/inventory-count/tasks"
            size="sm"
            variant="outline"
          >
            Powrót do kolejki
          </Button>
          <Button
            size="sm"
            colorScheme="blue"
            isDisabled={!canSave}
            isLoading={countMutation.isPending}
            onClick={() => {
              setLocalFormError("")
              countMutation.mutate(changedLines)
            }}
          >
            Zapisz policzone pozycje ({changedLines.length})
          </Button>
          <Button
            size="sm"
            colorScheme="teal"
            isDisabled={!canSubmit}
            isLoading={submitMutation.isPending}
            onClick={() => {
              if (!canSubmit) {
                if (hasUnsavedChanges) {
                  setLocalFormError(
                    "Najpierw zapisz policzone pozycje przed wysłaniem.",
                  )
                } else {
                  setLocalFormError(
                    "Uzupełnij poprawnie wszystkie ilości przed wysłaniem.",
                  )
                }
                return
              }
              setLocalFormError("")
              submitMutation.mutate()
            }}
          >
            Wyślij do weryfikacji
          </Button>
          <Button
            size="sm"
            colorScheme="green"
            isDisabled={!canReview}
            isLoading={
              reviewMutation.isPending &&
              reviewMutation.variables?.decision === "approve"
            }
            onClick={() => reviewMutation.mutate({ decision: "approve" })}
          >
            Zatwierdź
          </Button>
          <Button
            size="sm"
            colorScheme="orange"
            isDisabled={!canRecount}
            isLoading={recountMutation.isPending}
            onClick={() => {
              setRecountDialogError("")
              setRecountDialogOpen(true)
            }}
          >
            Poproś o ponowne przeliczenie
          </Button>
          <Button
            size="sm"
            colorScheme="red"
            variant="outline"
            isDisabled={!canCancel}
            isLoading={
              reviewMutation.isPending &&
              reviewMutation.variables?.decision === "reject"
            }
            onClick={() => {
              setCancelDialogError("")
              setCancelDialogOpen(true)
            }}
          >
            Odrzuć
          </Button>
          <Button
            size="sm"
            colorScheme="purple"
            isDisabled={!canPost}
            isLoading={postMutation.isPending}
            onClick={async () => {
              setLocalFormError("")
              try {
                await postMutation.mutateAsync()
              } catch (err: any) {
                const raw = String(
                  err?.body?.error ??
                    err?.message ??
                    "Nie udało się zaksięgować inwentaryzacji.",
                )
                if (
                  err?.body?.code ===
                    "inventory_count_legacy_reconciliation_blocked" ||
                  err?.body?.code === "inventory_count_target_alignment_blocked"
                ) {
                  setLocalFormError(
                    `Nie można bezpiecznie zaksięgować korekt. Po aktualizacji spróbuj ponownie. Jeśli blokada pozostaje, poproś o ponowne przeliczenie albo anuluj zadanie i przekaż administratorowi numer ${detail?.task_no ?? taskId}. Szczegóły dla wsparcia: ${raw}`,
                  )
                } else {
                  setLocalFormError(raw)
                }
              }
            }}
          >
            Zaksięguj korekty
          </Button>
        </Flex>

        {localFormError ? (
          <Text color="red.400" fontSize="sm" mt={2}>
            {localFormError}
          </Text>
        ) : null}

        <Box flex="1" minH={0} mt={2}>
          <WmsTableListCard
            isLoading={detailQuery.isPending}
            isFetching={detailQuery.isFetching}
            error={detailQuery.isError ? detailQuery.error : undefined}
            errorMessage="Nie udało się pobrać szczegółów zadania inwentaryzacji."
            isEmpty={
              !detailQuery.isPending &&
              !detailQuery.isError &&
              sortedLines.length === 0
            }
            emptyMessage="Brak linii zadania."
            minEmptyHeight={{ base: "200px", lg: "320px" }}
          >
            {isMobile ? (
              <Stack spacing={3} pb={canCount ? 20 : 0}>
                {sortedLines.map((line, idx) => (
                  <Box key={line.id} borderWidth="1px" borderRadius="xl" p={3}>
                    <Stack spacing={2}>
                      <Stack spacing={0}>
                        <Text fontSize="xs" color={mutedText}>
                          Lokacja: {line.location_code || "—"}
                        </Text>
                        <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>
                          {line.product_sku || "—"}
                        </Text>
                        <Text fontSize="xs" color={mutedText} noOfLines={1}>
                          {line.product_name || "Без назви"}
                        </Text>
                        {line.product_ean ? (
                          <Text fontSize="xs" color={mutedText} noOfLines={1}>
                            EAN: {line.product_ean}
                          </Text>
                        ) : null}
                        {line.lot_no ? (
                          <Text fontSize="xs" color={mutedText} noOfLines={1}>
                            Partia: {line.lot_no}
                          </Text>
                        ) : null}
                        {line.serial_no ? (
                          <Text fontSize="xs" color={mutedText} noOfLines={1}>
                            SN: {line.serial_no}
                          </Text>
                        ) : null}
                        <Text fontSize="xs" color={mutedText}>
                          Runda: {line.count_round}
                        </Text>
                      </Stack>
                      <SimpleGrid columns={2} spacing={2}>
                        <Box>
                          <Text fontSize="xs" color={mutedText}>
                            Ilość systemowa
                          </Text>
                          <Text fontSize="sm">
                            {line.system_qty_snapshot.toFixed(2)}
                          </Text>
                        </Box>
                        <Box>
                          <Text fontSize="xs" color={mutedText}>
                            Odchylenie
                          </Text>
                          <Text fontSize="sm">
                            {typeof line.variance_qty === "number"
                              ? line.variance_qty.toFixed(2)
                              : "—"}
                          </Text>
                        </Box>
                      </SimpleGrid>
                      <FormControl isInvalid={Boolean(lineErrors[line.id])}>
                        <Text fontSize="xs" color={mutedText} mb={1}>
                          Policzona ilość
                        </Text>
                        {canCount ? (
                          <Input
                            ref={(node) => {
                              inputRefs.current[line.id] = node
                            }}
                            autoFocus={idx === 0}
                            size="sm"
                            inputMode="decimal"
                            value={
                              editValues[line.id] ??
                              (typeof line.counted_qty === "number"
                                ? String(line.counted_qty)
                                : "")
                            }
                            onChange={(event) => {
                              setLocalFormError("")
                              setEditValues((prev) => ({
                                ...prev,
                                [line.id]: event.target.value,
                              }))
                            }}
                            onKeyDown={(event) => {
                              if (
                                event.key === "Enter" ||
                                event.key === "NumpadEnter"
                              ) {
                                event.preventDefault()
                                focusNext(line.id)
                              }
                            }}
                          />
                        ) : (
                          <Text fontSize="sm">
                            {typeof line.counted_qty === "number"
                              ? line.counted_qty.toFixed(2)
                              : "—"}
                          </Text>
                        )}
                        {lineErrors[line.id] ? (
                          <FormErrorMessage>
                            {lineErrors[line.id]}
                          </FormErrorMessage>
                        ) : null}
                      </FormControl>
                      <Flex align="center" justify="space-between">
                        <Badge
                          colorScheme={
                            line.count_state === "counted"
                              ? "blue"
                              : line.count_state === "approved"
                                ? "green"
                                : "gray"
                          }
                        >
                          {line.count_state}
                        </Badge>
                        <Text fontSize="xs" color={mutedText}>
                          {line.counted_at
                            ? new Date(line.counted_at).toLocaleString()
                            : "—"}
                        </Text>
                      </Flex>
                    </Stack>
                  </Box>
                ))}
              </Stack>
            ) : (
              <TableContainer maxH="58vh" overflowY="auto">
                <Table size="sm" sx={{ tableLayout: "fixed" }}>
                  <Thead bg={theadBg} position="sticky" top={0} zIndex={1}>
                    <Tr>
                      <Th w="170px">Lokacja</Th>
                      <Th w="270px">Pozycja</Th>
                      <Th w="130px" isNumeric>
                        Ilość systemowa
                      </Th>
                      <Th w="180px">Policzona ilość</Th>
                      <Th w="130px" isNumeric>
                        Odchylenie
                      </Th>
                      <Th w="130px">Stan</Th>
                      <Th w="180px">Policzono</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {sortedLines.map((line, idx) => (
                      <Tr
                        key={line.id}
                        bg={idx % 2 === 0 ? "transparent" : zebraBg}
                      >
                        <Td>
                          <Text fontSize="sm">{line.location_code || "—"}</Text>
                        </Td>
                        <Td>
                          <Stack spacing={0}>
                            <Text fontSize="sm" noOfLines={1}>
                              {line.product_sku || "—"}
                            </Text>
                            <Text fontSize="xs" color={mutedText} noOfLines={1}>
                              {line.product_name || "Без назви"}
                            </Text>
                            {line.product_ean ? (
                              <Text
                                fontSize="xs"
                                color={mutedText}
                                noOfLines={1}
                              >
                                EAN: {line.product_ean}
                              </Text>
                            ) : null}
                            {line.lot_no ? (
                              <Text
                                fontSize="xs"
                                color={mutedText}
                                noOfLines={1}
                              >
                                Partia: {line.lot_no}
                              </Text>
                            ) : null}
                            {line.serial_no ? (
                              <Text
                                fontSize="xs"
                                color={mutedText}
                                noOfLines={1}
                              >
                                SN: {line.serial_no}
                              </Text>
                            ) : null}
                            <Text fontSize="xs" color={mutedText}>
                              Runda: {line.count_round}
                            </Text>
                          </Stack>
                        </Td>
                        <Td isNumeric>{line.system_qty_snapshot.toFixed(2)}</Td>
                        <Td>
                          <FormControl isInvalid={Boolean(lineErrors[line.id])}>
                            {canCount ? (
                              <Input
                                ref={(node) => {
                                  inputRefs.current[line.id] = node
                                }}
                                size="sm"
                                textAlign="right"
                                inputMode="decimal"
                                value={
                                  editValues[line.id] ??
                                  (typeof line.counted_qty === "number"
                                    ? String(line.counted_qty)
                                    : "")
                                }
                                onChange={(event) => {
                                  setLocalFormError("")
                                  setEditValues((prev) => ({
                                    ...prev,
                                    [line.id]: event.target.value,
                                  }))
                                }}
                                onKeyDown={(event) => {
                                  if (
                                    event.key === "Enter" ||
                                    event.key === "NumpadEnter"
                                  ) {
                                    event.preventDefault()
                                    focusNext(line.id)
                                  }
                                }}
                              />
                            ) : typeof line.counted_qty === "number" ? (
                              line.counted_qty.toFixed(2)
                            ) : (
                              "—"
                            )}
                            {lineErrors[line.id] ? (
                              <FormErrorMessage>
                                {lineErrors[line.id]}
                              </FormErrorMessage>
                            ) : null}
                          </FormControl>
                        </Td>
                        <Td isNumeric>
                          {typeof line.variance_qty === "number"
                            ? line.variance_qty.toFixed(2)
                            : "—"}
                        </Td>
                        <Td>
                          <Badge
                            colorScheme={
                              line.count_state === "counted"
                                ? "blue"
                                : line.count_state === "approved"
                                  ? "green"
                                  : "gray"
                            }
                          >
                            {line.count_state}
                          </Badge>
                        </Td>
                        <Td>
                          {line.counted_at
                            ? new Date(line.counted_at).toLocaleString()
                            : "—"}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            )}
          </WmsTableListCard>
        </Box>

        {canCount ? (
          <Box
            position={{ base: "sticky", lg: "static" }}
            bottom={0}
            mt={3}
            p={3}
            borderWidth="1px"
            borderColor={stickyBorder}
            bg={stickyBg}
            borderRadius="xl"
            zIndex={3}
          >
            <Flex align="center" justify="space-between" gap={3} wrap="wrap">
              <Text fontSize="sm" color={mutedText}>
                Tryb skanowania: Enter przechodzi do następnego wiersza.
              </Text>
              <Flex gap={2}>
                <Button
                  size="sm"
                  colorScheme="blue"
                  isDisabled={!canSave}
                  isLoading={countMutation.isPending}
                  onClick={() => {
                    setLocalFormError("")
                    countMutation.mutate(changedLines)
                  }}
                >
                  Zapisz
                </Button>
                <Button
                  size="sm"
                  colorScheme="teal"
                  isDisabled={!canSubmit}
                  isLoading={submitMutation.isPending}
                  onClick={() => {
                    if (!canSubmit) {
                      if (hasUnsavedChanges) {
                        setLocalFormError(
                          "Najpierw zapisz policzone pozycje przed wysłaniem.",
                        )
                      } else {
                        setLocalFormError(
                          "Uzupełnij poprawnie wszystkie ilości przed wysłaniem.",
                        )
                      }
                      return
                    }
                    setLocalFormError("")
                    submitMutation.mutate()
                  }}
                >
                  Wyślij
                </Button>
              </Flex>
            </Flex>
          </Box>
        ) : null}
      </WmsPagePanel>
      <ActionAlertDialog
        isOpen={recountDialogOpen}
        onClose={() => {
          if (!recountMutation.isPending) {
            setRecountDialogOpen(false)
            setRecountDialogError("")
          }
        }}
        leastDestructiveRef={recountCancelRef}
        title="Poproś o ponowne przeliczenie"
        body={
          <Stack spacing={3}>
            <Text>
              Zostanie utworzona nowa runda na podstawie aktualnego stanu
              magazynu.
            </Text>
            <FormControl isRequired isInvalid={Boolean(recountDialogError)}>
              <Textarea
                ref={recountReasonRef}
                aria-label="Powód ponownego przeliczenia"
                maxLength={500}
                value={recountReason}
                onChange={(event) => {
                  setRecountReason(event.target.value)
                  setRecountDialogError(
                    event.target.value.trim() ? "" : "Powód jest wymagany.",
                  )
                }}
              />
              {recountDialogError ? (
                <FormErrorMessage>{recountDialogError}</FormErrorMessage>
              ) : null}
            </FormControl>
          </Stack>
        }
        confirmLabel="Utwórz nową rundę"
        confirmColorScheme="orange"
        confirmLoading={recountMutation.isPending}
        confirmDisabled={!recountReason.trim()}
        cancelDisabled={recountMutation.isPending}
        onConfirm={() => {
          const reason = recountReason.trim()
          if (!reason) {
            setRecountDialogError("Powód jest wymagany.")
            return
          }
          recountMutation.mutate(reason)
        }}
        isCentered
      />
      <ActionAlertDialog
        isOpen={cancelDialogOpen}
        onClose={() => {
          if (!reviewMutation.isPending) {
            setCancelDialogOpen(false)
            setCancelDialogError("")
          }
        }}
        leastDestructiveRef={cancelCancelRef}
        title="Odrzuć zadanie inwentaryzacji"
        body={
          <Stack spacing={3}>
            <Text>Zadanie zostanie anulowane bez zmiany stanu magazynu.</Text>
            <FormControl isRequired isInvalid={Boolean(cancelDialogError)}>
              <Textarea
                ref={cancelReasonRef}
                aria-label="Powód anulowania"
                maxLength={500}
                value={cancelReason}
                onChange={(event) => {
                  setCancelReason(event.target.value)
                  setCancelDialogError(
                    event.target.value.trim() ? "" : "Powód jest wymagany.",
                  )
                }}
              />
              {cancelDialogError ? (
                <FormErrorMessage>{cancelDialogError}</FormErrorMessage>
              ) : null}
            </FormControl>
          </Stack>
        }
        confirmLabel="Odrzuć zadanie"
        confirmColorScheme="red"
        confirmLoading={
          reviewMutation.isPending &&
          reviewMutation.variables?.decision === "reject"
        }
        confirmDisabled={!cancelReason.trim()}
        cancelDisabled={reviewMutation.isPending}
        onConfirm={() => {
          const reason = cancelReason.trim()
          if (!reason) {
            setCancelDialogError("Powód jest wymagany.")
            return
          }
          reviewMutation.mutate({ decision: "reject", reason })
        }}
        isCentered
      />
    </WmsPageShell>
  )
}

function StatCard({
  label,
  value,
  mutedText,
}: {
  label: string
  value: string
  mutedText: string
}) {
  return (
    <Box borderWidth="1px" borderRadius="xl" p={3}>
      <Text fontSize="sm" color={mutedText}>
        {label}
      </Text>
      <Text fontWeight="semibold">{value}</Text>
    </Box>
  )
}

function parseCountedQty(raw: string): { value?: number; error?: string } {
  const normalized = raw.trim()
  if (normalized.length === 0) {
    return { error: "Ilość jest wymagana" }
  }
  const parsed = Number(normalized.replace(",", "."))
  if (!Number.isFinite(parsed)) {
    return { error: "Dozwolone są tylko wartości liczbowe" }
  }
  if (parsed < 0) {
    return { error: "Ilość musi być >= 0" }
  }
  return { value: parsed }
}

function parseApiError(error: unknown, fallback: string): string {
  if (
    typeof error === "object" &&
    error !== null &&
    "body" in error &&
    typeof (error as { body?: { error?: string } }).body?.error === "string"
  ) {
    return (error as { body?: { error?: string } }).body?.error ?? fallback
  }
  return fallback
}

function remapCountPayload(
  payload: Array<{
    line_id: string
    counted_qty: number
    count_state?: string
  }>,
  previousLines: InventoryCountTaskLineDTO[],
  refreshedLines: InventoryCountTaskLineDTO[],
) {
  const refreshedByWarehouseLotID = new Map<string, InventoryCountTaskLineDTO>()
  const refreshedByIdentity = new Map<string, InventoryCountTaskLineDTO[]>()
  refreshedLines.forEach((line) => {
    if (line.warehouse_lot_id) {
      refreshedByWarehouseLotID.set(line.warehouse_lot_id, line)
    }
    const lines = refreshedByIdentity.get(countLineIdentity(line)) ?? []
    lines.push(line)
    refreshedByIdentity.set(countLineIdentity(line), lines)
  })
  const previousOccurrenceByLineID = new Map<string, number>()
  const previousCountsByIdentity = new Map<string, number>()
  previousLines.forEach((line) => {
    const identity = countLineIdentity(line)
    const occurrence = previousCountsByIdentity.get(identity) ?? 0
    previousOccurrenceByLineID.set(line.id, occurrence)
    previousCountsByIdentity.set(identity, occurrence + 1)
  })

  return payload.map((entry) => {
    const previousLine = previousLines.find((line) => line.id === entry.line_id)
    if (!previousLine) {
      throw new Error("Nie znaleziono poprzedniej pozycji inwentaryzacji.")
    }
    const occurrence = previousOccurrenceByLineID.get(previousLine.id)
    const refreshedLine = previousLine.warehouse_lot_id
      ? refreshedByWarehouseLotID.get(previousLine.warehouse_lot_id)
      : occurrence === undefined
        ? undefined
        : refreshedByIdentity.get(countLineIdentity(previousLine))?.[occurrence]
    if (!refreshedLine) {
      throw new Error(
        "Pozycja zmieniła się podczas uruchamiania zadania. Odśwież stronę i spróbuj ponownie.",
      )
    }
    return { ...entry, line_id: refreshedLine.id }
  })
}

function countLineIdentity(line: InventoryCountTaskLineDTO): string {
  return [
    line.product_id,
    line.location_code.trim().toUpperCase(),
    line.lot_no?.trim() ?? "",
    line.serial_no?.trim() ?? "",
    line.count_round,
  ].join("|")
}

function compareLocationCodes(
  left?: string | null,
  right?: string | null,
): number {
  const a = (left || "").trim()
  const b = (right || "").trim()
  if (!a && !b) {
    return 0
  }
  if (!a) {
    return 1
  }
  if (!b) {
    return -1
  }

  const tokensA = tokenizeLocation(a)
  const tokensB = tokenizeLocation(b)
  const maxLen = Math.max(tokensA.length, tokensB.length)
  for (let i = 0; i < maxLen; i += 1) {
    const tokenA = tokensA[i]
    const tokenB = tokensB[i]
    if (tokenA === undefined) {
      return -1
    }
    if (tokenB === undefined) {
      return 1
    }
    const numericA = /^[0-9]+$/.test(tokenA)
    const numericB = /^[0-9]+$/.test(tokenB)
    if (numericA && numericB) {
      const numA = Number(tokenA)
      const numB = Number(tokenB)
      if (numA !== numB) {
        return numA - numB
      }
      continue
    }
    const cmp = tokenA.localeCompare(tokenB, "en", {
      sensitivity: "base",
      numeric: true,
    })
    if (cmp !== 0) {
      return cmp
    }
  }

  return a.localeCompare(b, "en", { sensitivity: "base", numeric: true })
}

function tokenizeLocation(value: string): string[] {
  return value
    .toUpperCase()
    .split(/[^A-Z0-9]+/)
    .flatMap((segment) => segment.match(/[A-Z]+|[0-9]+/g) ?? [])
}
